/**
 * Wire shapes for browsing a job's Dropbox folder from the report screen.
 *
 * Reviewing a legacy-process report used to mean two windows: the dashboard
 * for the report, Dropbox for the evidence it was built from. These let the
 * report screen show the folder in place — list it, preview the checklist
 * scan or a photo, and attach a file straight onto a clause or prompt.
 *
 * Read-only against Dropbox (list + download). The only write is into OUR
 * photo store, via the attach endpoint. Retires with the Dropbox adapter.
 */
import { z } from 'zod';
/** One entry in a job folder listing. */
export const DropboxBrowseEntrySchema = z.object({
    name: z.string(),
    /** Dropbox-absolute path. Opaque to the client — pass back verbatim. */
    path: z.string(),
    kind: z.enum(['file', 'folder']),
    /** Bytes. Files only. */
    size: z.number().int().nonnegative().optional(),
    /** ISO timestamp from Dropbox metadata. Files only. */
    modified: z.string().optional(),
    /**
     * Whether the file preview endpoint can render this: images and PDFs.
     * Folders and anything else are false, so the UI does not offer a
     * preview it cannot serve.
     */
    previewable: z.boolean(),
    /** Whether this file may be attached to the report (images + PDF). */
    attachable: z.boolean(),
});
export const DropboxBrowseResponseSchema = z.object({
    /** The job's own folder — the containment root for every path below. */
    jobFolderPath: z.string(),
    /** The folder actually listed (the root, or a subfolder within it). */
    path: z.string(),
    /** Parent path, absent when `path` IS the job folder. */
    parentPath: z.string().optional(),
    entries: z.array(DropboxBrowseEntrySchema),
});
/** Attach one Dropbox file into the job's photo store under `promptKey`. */
export const DropboxAttachRequestSchema = z.object({
    path: z.string().min(1),
    promptKey: z.string().min(1),
});
//# sourceMappingURL=dropbox-browse.js.map